import { BuildClass, DateTimeObj, guid, TW_OVERFLOW_ELLIPSIS, } from '../../../../universal';
import { _, React } from '../../../lib';
import { Button } from '../buttons';
import { AgentUI, getChatCSSVars, getPendingChangeStatus, hasPendingApproval, MarkdownRenderer, PendingChangesTray, } from '../chat';
import { Checkbox } from '../checkbox';
import { PromptConfirm } from '../confirm';
import { setContextMenu } from '../context-menu';
import { GridCell } from '../editable-cells';
import { EditableGrid } from '../editable-grid';
import { FormComponent, FormType, openFlyoutForm } from '../form';
import { useRSInstance } from '../meta-types';
import { NoteAttachment } from '../notes';
import { ResizeSplitter } from '../resize-split';
import { SidebarListGrid } from '../sidebar-listgrid';
import { TabControl } from '../tabs';
import { StubText } from '../text';
import { TextareaWithVoice } from '../text-with-voice';
import { Textarea, Textbox } from '../textbox';
import { ToolbarNew } from '../toolbar';
import { UsageLimitDonut } from '../usage-limit-donut';
import { SYNTHETIC_ROW_IDS, } from './types';
export * from './types';
const STATUS_LABELS = {
    50: 'Archived',
    80: 'Unapproved',
    100: 'Pending',
    125: 'Categorising',
    150: 'Categorised',
    175: 'Processing',
    200: 'Collaboration',
    601: 'Questions',
    602: 'Partial Approval',
    603: 'Full Approval',
    900: 'Completed',
};
const TASK_NOTE_MAX_LENGTH = 50000;
const getDefaultState = () => ({
    selectedRowID: SYNTHETIC_ROW_IDS.DASHBOARD,
    currentTab: 'details',
    currentSettingsTab: 'email',
    currentQuickOnlyTab: 'chat',
    sblgView: {},
    showUnapproved: false,
    statusFilter: null,
    isPrimaryNoteEditing: false,
    isSecondaryNoteEditing: false,
    draftPrimaryNote: '',
    draftSecondaryNote: '',
    newReceiverName: '',
    selectedAttachmentID: null,
    pendingChangesStates: {},
    attachmentSplitWidths: null,
});
export const AgentConsole = (props) => {
    const rs = useRSInstance({
        props,
        defaultState: getDefaultState,
        refs: {},
        actionToDelta: null,
    });
    const syntheticRows = [
        {
            type: 'synthetic',
            ID: SYNTHETIC_ROW_IDS.DASHBOARD,
            label: 'Dashboard',
            icon: '/static/img/i8/color-combo-chart.svg',
        },
        {
            type: 'synthetic',
            ID: SYNTHETIC_ROW_IDS.QUICK_AGENTS,
            label: 'Quick Agents',
            icon: '/static/img/i8/color-chatbot.svg',
        },
        {
            type: 'synthetic',
            ID: SYNTHETIC_ROW_IDS.SETTINGS,
            label: 'Settings',
            icon: '/static/img/i8/color-settings.svg',
        },
        {
            type: 'synthetic',
            ID: SYNTHETIC_ROW_IDS.TEMPLATES,
            label: 'Templates',
            icon: '/static/img/i8/color-to-do.svg',
        },
    ];
    const filteredTasks = _.chain(props.data.tasks)
        .filter(task => {
        if (!rs.state.showUnapproved && task.Status === 80) {
            return false;
        }
        if (rs.state.statusFilter != null && task.Status !== rs.state.statusFilter) {
            return false;
        }
        return true;
    })
        .value();
    const taskRows = filteredTasks.map(task => ({
        type: 'task',
        data: task,
    }));
    const sidebarData = [...syntheticRows, ...taskRows];
    const getRowID = (row) => row.type === 'synthetic' ? row.ID : row.data.ID;
    const sidebarFields = [
        {
            key: 'icon',
            lbl: '',
            width: { init: 24 },
            text: () => '',
            display: row => row.type === 'synthetic' ? (React.createElement("img", { src: row.icon, alt: "", className: "w-4 h-4" })) : (React.createElement("div", { className: BuildClass({
                    'w-2 h-2 rounded-full': true,
                    'bg-green-500': row.data.Status === 900,
                    'bg-blue-500': row.data.Status >= 100 && row.data.Status < 200,
                    'bg-yellow-500': row.data.Status >= 200 && row.data.Status < 900,
                    'bg-red-500': row.data.Status === 80 ||
                        (row.data.Status >= 601 && row.data.Status <= 603),
                    'bg-neutral-400': row.data.Status === 50,
                }) })),
            groupVal: row => (row.type === 'synthetic' ? 'nav' : 'tasks'),
            groupName: groupID => (groupID === 'nav' ? '' : 'Tasks'),
            groupSort: group => (group.ID === 'nav' ? 0 : 1),
        },
        {
            key: 'title',
            lbl: 'Name',
            text: row => row.type === 'synthetic'
                ? row.label
                : (row.data.Title ?? 'Untitled Task'),
            className: () => TW_OVERFLOW_ELLIPSIS,
            sortVal: row => row.type === 'synthetic' ? `0-${row.label}` : `1-${row.data.Title ?? ''}`,
        },
        {
            key: 'status',
            lbl: 'Status',
            width: { init: 70 },
            text: row => row.type === 'synthetic' ? '' : (STATUS_LABELS[row.data.Status] ?? ''),
            className: () => 'text-xs text-neutral-500',
        },
        {
            key: 'changes',
            lbl: 'Δ',
            lblDesc: 'Pending changes',
            width: { init: 30 },
            text: row => row.type === 'task' && row.data.PendingChangesCount > 0
                ? String(row.data.PendingChangesCount)
                : '',
            className: () => 'text-right text-xs',
        },
    ];
    const renderMainContent = () => {
        const selectedID = rs.state.selectedRowID;
        if (!selectedID) {
            return (React.createElement("div", { className: "flex-1 flex items-center justify-center" },
                React.createElement(StubText, null, "Select an item from the sidebar")));
        }
        if (selectedID === SYNTHETIC_ROW_IDS.DASHBOARD) {
            return React.createElement(DashboardPane, null);
        }
        if (selectedID === SYNTHETIC_ROW_IDS.QUICK_AGENTS) {
            return React.createElement(QuickAgentsPane, { rs: rs });
        }
        if (selectedID === SYNTHETIC_ROW_IDS.SETTINGS) {
            return React.createElement(SettingsPane, { rs: rs });
        }
        if (selectedID === SYNTHETIC_ROW_IDS.TEMPLATES) {
            return React.createElement(TemplatesPane, null);
        }
        const taskDetails = props.data.taskDetails[selectedID];
        if (!taskDetails) {
            return (React.createElement("div", { className: "flex-1 flex items-center justify-center" },
                React.createElement(StubText, null, "Task not found")));
        }
        return React.createElement(TaskDetailPane, { rs: rs, taskID: selectedID, taskDetails: taskDetails });
    };
    const statusFilterOptions = [
        { key: null, lbl: 'All Statuses' },
        { key: 100, lbl: 'Pending' },
        { key: 125, lbl: 'Categorising' },
        { key: 150, lbl: 'Categorised' },
        { key: 175, lbl: 'Processing' },
        { key: 200, lbl: 'Collaboration' },
        { key: 601, lbl: 'Questions' },
        { key: 602, lbl: 'Partial Approval' },
        { key: 603, lbl: 'Full Approval' },
        { key: 900, lbl: 'Completed' },
    ];
    const toolbarLhs = _.compact([
        {
            type: 'combobox-strkey',
            width: 130,
            importance: 5,
            options: statusFilterOptions.map(o => ({
                value: String(o.key ?? ''),
                text: o.lbl,
            })),
            value: String(rs.state.statusFilter ?? ''),
            onUpdate: (v) => {
                rs.updateState({ statusFilter: v ? Number(v) : null });
            },
        },
    ]);
    const toolbarRhs = _.compact([
        {
            type: 'checkbox',
            width: 140,
            importance: 10,
            lbl: 'Show Unapproved',
            value: rs.state.showUnapproved,
            onUpdate: (v) => {
                rs.updateState({ showUnapproved: v });
            },
        },
    ]);
    const openCreateTaskFlyout = React.useCallback(() => {
        openFlyoutForm({
            title: 'New Task',
            size: [520, 560],
            lblWidth: 80,
            formPrompt: 'Create a new task',
            defaultValue: () => ({
                title: '',
                note: '',
                allowAgents: false,
                attachments: [],
            }),
            fields: {
                title: FormType.Text({
                    lbl: 'Title',
                    maxLength: 80,
                    validators: [
                        {
                            req: (value) => value.trim().length > 0,
                            msg: 'is required',
                        },
                    ],
                }),
                note: FormType.Textarea({
                    lbl: 'Details',
                    rows: 6,
                    maxLength: TASK_NOTE_MAX_LENGTH,
                    placeholder: 'Describe the task...',
                }),
                allowAgents: FormType.Checkbox({
                    lbl: 'Allow Agent Automation',
                }),
                attachments: FormType.FileUploadMultiple({
                    lbl: 'Attachments',
                    height: 120,
                    fullWidth: true,
                    storageMode: 'v3',
                }),
            },
            onSave: (model, resolve) => {
                const uploadedItems = model.current.attachments.map(item => ({
                    guid: item.FileGuid ?? '',
                    filename: item.Filename,
                    mimeType: item.MimeType || 'application/octet-stream',
                }));
                if (uploadedItems.some(item => item.guid.length === 0)) {
                    resolve('Attachment missing GUID');
                    return;
                }
                props.taskCallbacks.onCreateTask({
                    title: model.current.title.trim(),
                    primaryNote: model.current.note,
                    allowAgents: model.current.allowAgents,
                    callbacks: {
                        onComplete: taskID => {
                            rs.updateState({ selectedRowID: taskID });
                            if (uploadedItems.length === 0) {
                                resolve(true);
                                return;
                            }
                            Promise.all(uploadedItems.map(item => new Promise((done, reject) => {
                                props.attachmentCallbacks.onAddAttachment({
                                    taskID,
                                    file: new File([], item.filename, {
                                        type: item.mimeType,
                                    }),
                                    fileGuid: item.guid,
                                    callbacks: {
                                        onComplete: () => {
                                            done();
                                        },
                                        onError: error => {
                                            reject(new Error(error));
                                        },
                                    },
                                });
                            })))
                                .then(() => {
                                resolve(true);
                            })
                                .catch(error => {
                                resolve(error instanceof Error
                                    ? `Failed to register attachments: ${error.message}`
                                    : `Failed to register attachments: ${String(error)}`);
                            });
                        },
                        onError: error => {
                            resolve(error);
                        },
                    },
                });
            },
        });
    }, [props.attachmentCallbacks, props.taskCallbacks, rs]);
    if (props.quickOnly) {
        return React.createElement(QuickOnlyPane, { rs: rs });
    }
    return (React.createElement(SidebarListGrid, { pk: getRowID, data: sidebarData, fields: sidebarFields, buildForm: () => renderMainContent(), multiple: false, value: rs.state.selectedRowID, onUpdateValue: v => {
            rs.updateState({ selectedRowID: v });
        }, view: rs.state.sblgView, onUpdateView: v => {
            rs.updateState(s => ({ sblgView: { ...s.sblgView, ...v } }));
        }, adding: {
            lbl: { text: 'New Task', width: 85 },
            onClick: () => {
                openCreateTaskFlyout();
            },
        }, inactivity: {
            lbl: { text: 'Archived', width: 80 },
            isInactive: row => row.type === 'task' && row.data.Status === 50,
            className: 'text-neutral-400',
        }, sidebarWidth: {
            defaultWidth: 240,
            minWidth: 160,
            maxWidth: 600,
        }, listGridSettings: {
            defaultSortingKey: 'title',
        }, toolbarExtra: {
            lhs: toolbarLhs,
            rhs: toolbarRhs,
        }, stubMessage: { lbl: 'Select an item' } }));
};
const QuickOnlyPane = ({ rs }) => {
    const tabs = [
        {
            value: 'chat',
            label: 'Chat',
            content: () => React.createElement(QuickAgentsPane, { rs: rs }),
        },
        {
            value: 'skills',
            label: 'Skills',
            content: () => React.createElement(SkillsSettingsTab, { rs: rs }),
        },
        {
            value: 'commands',
            label: 'Commands',
            content: () => rs.props.quickCommandsPane?.() ?? React.createElement(CommandsPlaceholderPane, null),
        },
    ];
    return (React.createElement("div", { className: "h-full min-h-0 flex flex-col" },
        React.createElement(TabControl, { tabs: tabs, value: rs.state.currentQuickOnlyTab, onUpdate: value => {
                rs.updateState({ currentQuickOnlyTab: value });
            }, className: "flex-1 min-h-0" })));
};
const CommandsPlaceholderPane = () => (React.createElement("div", { className: "h-full flex items-center justify-center p-6 text-neutral-600" },
    React.createElement("div", { className: "max-w-md rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-5 text-center shadow-sm" },
        React.createElement("img", { src: "/static/img/i8/color-to-do.svg", alt: "", className: "mx-auto mb-3 h-10 w-10 opacity-60" }),
        React.createElement("h2", { className: "mb-2 text-base font-medium text-neutral-800" }, "No commands available"),
        React.createElement("p", { className: "text-sm leading-5" }, "This view does not have command shortcuts configured yet."))));
const DashboardPane = () => (React.createElement("div", { className: "h-full flex flex-col items-center justify-center p-8 text-neutral-500" },
    React.createElement("img", { src: "/static/img/i8/color-combo-chart.svg", alt: "", className: "w-16 h-16 mb-4 opacity-50" }),
    React.createElement("h2", { className: "text-lg font-medium mb-2" }, "Dashboard"),
    React.createElement("p", { className: "text-sm text-center max-w-md" }, "Analytics and queue insights will be displayed here in a future update.")));
const TemplatesPane = () => (React.createElement("div", { className: "h-full flex flex-col items-center justify-center p-8 text-neutral-500" },
    React.createElement("img", { src: "/static/img/i8/color-to-do.svg", alt: "", className: "w-16 h-16 mb-4 opacity-50" }),
    React.createElement("h2", { className: "text-lg font-medium mb-2" }, "Templates"),
    React.createElement("p", { className: "text-sm text-center max-w-md" }, "Task templates and automation rules will be configured here in a future update.")));
const QuickAgentsPane = ({ rs }) => {
    const props = rs.props;
    const quickAgentThreads = _.chain(props.data.agentThreads)
        .pickBy(thread => !thread.metadata.parentTask)
        .value();
    return (React.createElement("div", { className: "h-full" },
        React.createElement(AgentUI, { threads: quickAgentThreads, currentUserID: props.currentUserID, facility: props.facility, subFacility: props.subFacility ?? 0, agentType: props.applicationType, changeRenderers: props.changeRenderers, widgetToGroupCode: props.widgetToGroupCode, toolRenderers: props.toolRenderers, toolHandlers: props.toolHandlers, theme: props.theme, hue: props.hue, onSendMessage: input => {
                props.agentThreadCallbacks.onSendMessage({
                    threadID: input.threadID,
                    message: input.message,
                    persistMessage: input.persistMessage,
                    pendingChanges: input.pendingChanges,
                    pendingQuestions: input.pendingQuestions,
                    useBigModel: input.useBigModel,
                    callbacks: input.callbacks,
                });
            }, onApproveChanges: input => {
                props.agentThreadCallbacks.onApproveChanges({
                    threadIDs: input.threadIDs,
                    approvalIDs: input.approvalIDs,
                    callbacks: input.callbacks,
                });
            }, onRejectChanges: input => {
                props.agentThreadCallbacks.onRejectChanges({
                    approvalIDs: input.approvalIDs,
                    callbacks: input.callbacks,
                });
            }, onLoadThreadContent: props.agentThreadCallbacks.onLoadThreadContent
                ? input => {
                    props.agentThreadCallbacks.onLoadThreadContent?.(input);
                }
                : undefined, onSubmitQuestions: props.agentThreadCallbacks.onSubmitQuestions
                ? input => {
                    props.agentThreadCallbacks.onSubmitQuestions?.(input);
                }
                : undefined, onThreadAction: props.agentThreadCallbacks.onThreadAction
                ? input => {
                    props.agentThreadCallbacks.onThreadAction?.({
                        threadIDs: input.threadIDs,
                        action: input.action,
                        callbacks: input.callbacks,
                    });
                }
                : undefined, onDeleteMessage: rs.props.agentThreadCallbacks.onDeleteMessage
                ? input => {
                    rs.props.agentThreadCallbacks.onDeleteMessage?.(input);
                }
                : undefined })));
};
const SettingsPane = ({ rs }) => {
    const tabs = [
        {
            value: 'email',
            label: 'Email',
            content: () => React.createElement(EmailSettingsTab, { rs: rs }),
        },
        {
            value: 'skills',
            label: 'Skills',
            content: () => React.createElement(SkillsSettingsTab, { rs: rs }),
        },
    ];
    return (React.createElement("div", { className: "h-full flex flex-col" },
        React.createElement(TabControl, { tabs: tabs, value: rs.state.currentSettingsTab, onUpdate: v => {
                rs.updateState({ currentSettingsTab: v });
            }, className: "flex-1" })));
};
const EmailSettingsTab = ({ rs }) => {
    const props = rs.props;
    const [selectedReceiverID, setSelectedReceiverID] = React.useState(null);
    const [isCreating, setIsCreating] = React.useState(false);
    const previewHex = React.useMemo(() => guid().slice(0, 8), []);
    const receivers = props.data.emailReceivers;
    const selectedAllowlist = selectedReceiverID
        ? (props.data.emailAllowlist[selectedReceiverID] ?? [])
        : [];
    const selectedReceiver = selectedReceiverID
        ? (receivers.find(receiver => receiver.ID === selectedReceiverID) ?? null)
        : null;
    const generateEmailPreview = (friendlyName) => {
        if (!friendlyName) {
            return `<name>-${props.appName}-<hex>@trionline.com.au`;
        }
        return `${friendlyName}-${props.appName}-${previewHex}@trionline.com.au`;
    };
    const isValidFriendlyName = (name) => name.length > 0 && name.length <= 8 && /^[a-zA-Z0-9]+$/.test(name);
    const handleCreateReceiver = () => {
        const name = rs.state.newReceiverName.trim().toLowerCase();
        if (!isValidFriendlyName(name)) {
            return;
        }
        setIsCreating(true);
        props.emailSettingsCallbacks.onCreateReceiver({
            friendlyName: name,
            callbacks: {
                onComplete: receiver => {
                    rs.updateState({ newReceiverName: '' });
                    setSelectedReceiverID(receiver.ID);
                    setIsCreating(false);
                },
                onError: err => {
                    console.error('Failed to create receiver:', err);
                    setIsCreating(false);
                },
            },
        });
    };
    const allowlistFields = [
        GridCell.Text({
            key: 'Name',
            lbl: 'Name',
            value: x => x.Name ?? '',
            onUpdate: (v, r) => {
                r.Name = v;
            },
        }),
        GridCell.Text({
            key: 'SenderAddress',
            lbl: 'Sender Address',
            value: x => x.SenderAddress,
            onUpdate: (v, r) => {
                r.SenderAddress = v;
            },
        }),
    ];
    const friendlyNameTrimmed = rs.state.newReceiverName.trim().toLowerCase();
    const isValid = isValidFriendlyName(friendlyNameTrimmed);
    const hasInput = rs.state.newReceiverName.length > 0;
    return (React.createElement("div", { className: "h-full flex flex-col" },
        React.createElement("div", { className: "border-b border-neutral-200 px-4 py-3" },
            React.createElement("h2", { className: "text-lg font-medium" }, "Email Inbox Settings"),
            React.createElement("p", { className: "text-sm text-neutral-500 mt-1" }, "Manage email receivers and sender allowlists for task creation.")),
        React.createElement("div", { className: "flex-1 flex overflow-hidden" },
            React.createElement("div", { className: "w-80 border-r border-neutral-200 flex flex-col" },
                React.createElement("div", { className: "p-3 border-b border-neutral-100" },
                    React.createElement("h3", { className: "text-sm font-medium mb-2" }, "Add Email Receiver"),
                    React.createElement("p", { className: "text-xs text-neutral-500 mb-2" },
                        "Format:",
                        ' ',
                        React.createElement("code", { className: "bg-neutral-100 px-1 rounded" }, generateEmailPreview(''))),
                    React.createElement("div", { className: "flex gap-2" },
                        React.createElement(Textbox, { className: "flex-1", placeholder: "Friendly name", value: rs.state.newReceiverName, onUpdate: v => {
                                rs.updateState({
                                    newReceiverName: v.replace(/[^a-zA-Z0-9]/g, ''),
                                });
                            }, maxLength: 8 }),
                        React.createElement(Button, { type: "submit", disabled: isCreating || !isValid, onClick: handleCreateReceiver }, "Add")),
                    hasInput && (React.createElement("div", { className: BuildClass({
                            'text-xs mt-1': true,
                            'text-neutral-500': isValid,
                            'text-red-500': !isValid,
                        }) }, isValid
                        ? `Will create: ${generateEmailPreview(friendlyNameTrimmed)}`
                        : 'Name must be 1-8 alphanumeric characters'))),
                React.createElement("div", { className: "p-2 border-b border-neutral-100 bg-neutral-50" },
                    React.createElement("h4", { className: "text-xs font-medium text-neutral-600" }, "Configured Receivers")),
                React.createElement("div", { className: "flex-1 overflow-y-auto" }, receivers.length === 0 ? (React.createElement("div", { className: "p-4 text-sm text-neutral-500 text-center" }, "No email receivers configured")) : (React.createElement("div", { className: "divide-y divide-neutral-100" }, receivers.map(receiver => (React.createElement("div", { key: receiver.ID, className: BuildClass({
                        'p-3 cursor-pointer hover:bg-neutral-50': true,
                        'bg-blue-50': selectedReceiverID === receiver.ID,
                    }), onClick: () => {
                        setSelectedReceiverID(receiver.ID);
                    } },
                    React.createElement("div", { className: "flex items-center gap-2" },
                        React.createElement(Checkbox, { lbl: "", value: !receiver.IsDisabled, onUpdate: () => {
                                props.emailSettingsCallbacks.onToggleReceiver({
                                    receiverID: receiver.ID,
                                    isDisabled: !receiver.IsDisabled,
                                    callbacks: {
                                        onComplete: () => { },
                                        onError: err => {
                                            console.error(err);
                                        },
                                    },
                                });
                            }, title: receiver.IsDisabled
                                ? 'Enable receiver'
                                : 'Disable receiver' }),
                        React.createElement("div", { className: "flex-1 min-w-0" },
                            React.createElement("div", { className: BuildClass({
                                    'text-sm font-medium truncate': true,
                                    'text-neutral-400': receiver.IsDisabled,
                                }) }, receiver.Description ??
                                receiver.EmailAddress.split('@')[0]),
                            React.createElement("div", { className: "text-xs text-neutral-500 truncate" }, receiver.EmailAddress)))))))))),
            React.createElement("div", { className: "flex-1 flex flex-col" }, selectedReceiver ? (React.createElement(React.Fragment, null,
                React.createElement("div", { className: "border-b border-neutral-100 px-4 py-3" },
                    React.createElement("h3", { className: "text-sm font-medium" }, "Sender Allowlist"),
                    React.createElement("p", { className: "text-xs text-neutral-500 mt-1" },
                        "Allowed senders for",
                        ' ',
                        React.createElement("span", { className: "font-medium" }, selectedReceiver.EmailAddress))),
                React.createElement("div", { className: "flex-1 p-4 overflow-y-auto" },
                    React.createElement(EditableGrid, { className: "w-full", data: selectedAllowlist, pk: entry => entry.ID, defaultSortingKey: "SenderAddress", deletable: true, appendable: () => ({
                            ID: null,
                            EmailAddressID: selectedReceiver.ID,
                            SenderAddress: '',
                            Name: '',
                        }), fields: allowlistFields, onSave: (delta, resolve) => {
                            const appendedPromises = _.chain(delta.appended)
                                .map(async (entry) => new Promise((res, rej) => {
                                props.emailSettingsCallbacks.onAddAllowlistEntry({
                                    receiverID: selectedReceiver.ID,
                                    name: entry.Name ?? '',
                                    senderAddress: entry.SenderAddress ??
                                        '',
                                    callbacks: {
                                        onComplete: () => {
                                            res();
                                        },
                                        onError: err => {
                                            rej(new Error(err));
                                        },
                                    },
                                });
                            }))
                                .value();
                            const updatedPromises = _.chain(delta.updated)
                                .map(async (updates, key) => {
                                const existing = selectedAllowlist.find(entry => entry.ID === Number(key));
                                if (!existing) {
                                    return null;
                                }
                                const name = updates.Name ?? existing.Name ?? '';
                                const senderAddress = updates.SenderAddress ??
                                    existing.SenderAddress;
                                return new Promise((res, rej) => {
                                    props.emailSettingsCallbacks.onDeleteAllowlistEntry({
                                        entryID: existing.ID,
                                        callbacks: {
                                            onComplete: () => {
                                                props.emailSettingsCallbacks.onAddAllowlistEntry({
                                                    receiverID: selectedReceiver.ID,
                                                    name,
                                                    senderAddress,
                                                    callbacks: {
                                                        onComplete: () => {
                                                            res();
                                                        },
                                                        onError: err => {
                                                            rej(new Error(err));
                                                        },
                                                    },
                                                });
                                            },
                                            onError: err => {
                                                rej(new Error(err));
                                            },
                                        },
                                    });
                                });
                            })
                                .compact()
                                .value();
                            const deletedPromises = _.chain(delta.deleted)
                                .map(async (id) => new Promise((res, rej) => {
                                props.emailSettingsCallbacks.onDeleteAllowlistEntry({
                                    entryID: id,
                                    callbacks: {
                                        onComplete: () => {
                                            res();
                                        },
                                        onError: err => {
                                            rej(new Error(err));
                                        },
                                    },
                                });
                            }))
                                .value();
                            const promises = _.concat(appendedPromises, updatedPromises, deletedPromises);
                            if (promises.length === 0) {
                                resolve(true);
                                return;
                            }
                            Promise.all(promises)
                                .then(() => {
                                resolve(true);
                            })
                                .catch(err => {
                                resolve(String(err));
                            });
                        } })))) : (React.createElement("div", { className: "flex-1 flex items-center justify-center text-neutral-500" }, "Select a receiver to manage its allowlist"))))));
};
const SkillsSettingsTab = ({ rs }) => {
    const props = rs.props;
    const [selectedSkillID, setSelectedSkillID] = React.useState(null);
    const [skillsFormMode, setSkillsFormMode] = React.useState('read');
    const [skillsSblgView, setSkillsSblgView] = React.useState({
        listGridView: {
            hiddenFields: ['updated'],
        },
    });
    const [skillsFormMessage, setSkillsFormMessage] = React.useState('');
    const [skillsFormLoading, setSkillsFormLoading] = React.useState(null);
    const pendingCreatedSkillID = React.useRef(null);
    const fullSkillTextareaRef = React.useRef(null);
    const skillsEditFormRef = React.useRef(null);
    const scopedSubFacility = props.skillsUseSubFacilityScope
        ? (props.subFacility ?? null)
        : null;
    const isInternalUser = rs.props.currentUserID < 100000;
    const isSkillGlobalOverrideUser = [24222, 49067].includes(rs.props.currentUserID);
    const scopedSkills = _.filter(props.data.skills, skill => skill.Facility === props.facility &&
        (skill.SubFacility ?? null) === scopedSubFacility &&
        skill.ApplicationType === props.applicationType);
    const globalSkills = _.filter(props.data.skills, skill => (skill.Facility === 0 || skill.Facility == null) &&
        (skill.SubFacility ?? null) === null &&
        skill.ApplicationType === props.applicationType);
    const visibleSkills = _.sortBy(_.uniqBy([...scopedSkills, ...(isInternalUser ? globalSkills : [])], skill => skill.ID), skill => skill.Title.toLocaleLowerCase());
    const selectedSkill = selectedSkillID == null
        ? null
        : (visibleSkills.find(skill => String(skill.ID) === selectedSkillID) ?? null);
    const isGlobalSelected = (selectedSkill?.Facility === 0 || selectedSkill?.Facility == null) &&
        (selectedSkill?.SubFacility ?? null) === null;
    const isGlobalReadOnly = isGlobalSelected && !isSkillGlobalOverrideUser;
    React.useEffect(() => {
        const hasSelectedSkill = selectedSkillID != null &&
            visibleSkills.some(skill => String(skill.ID) === selectedSkillID);
        if (hasSelectedSkill) {
            if (pendingCreatedSkillID.current === selectedSkillID) {
                pendingCreatedSkillID.current = null;
            }
            return;
        }
        if (selectedSkillID != null &&
            pendingCreatedSkillID.current === selectedSkillID) {
            return;
        }
        const fallbackSkillID = _.head(visibleSkills)
            ? String(_.head(visibleSkills)?.ID)
            : null;
        if (fallbackSkillID !== selectedSkillID) {
            setSelectedSkillID(fallbackSkillID);
        }
    }, [selectedSkillID, visibleSkills]);
    const selectedSkillFormValue = React.useMemo(() => ({
        title: selectedSkill?.Title ?? '',
        description: selectedSkill?.Description ?? '',
        document: selectedSkill?.Document ?? '',
    }), [
        selectedSkill?.Description,
        selectedSkill?.Document,
        selectedSkill?.ID,
        selectedSkill?.Title,
    ]);
    const [skillFormValue, setSkillFormValue] = React.useState(selectedSkillFormValue);
    React.useEffect(() => {
        if (skillsFormMode === 'edit') {
            return;
        }
        setSkillFormValue(selectedSkillFormValue);
    }, [selectedSkillFormValue, skillsFormMode]);
    React.useEffect(() => {
        setSkillsFormMode('read');
        setSkillsFormMessage('');
        setSkillsFormLoading(null);
    }, [selectedSkillID]);
    const sidebarFields = [
        {
            key: 'title',
            lbl: 'Skill',
            text: skill => skill.Title,
            className: () => TW_OVERFLOW_ELLIPSIS,
            sortVal: skill => skill.Title.toLocaleLowerCase(),
            groupVal: skill => skill.Facility === 0 || skill.Facility == null ? 'global' : 'scoped',
            groupName: groupID => (groupID === 'global' ? 'Global' : 'Scoped'),
            groupSort: group => (group.ID === 'global' ? 0 : 1),
        },
        {
            key: 'updated',
            lbl: 'Updated',
            width: { init: 120 },
            text: skill => DateTimeObj.parse(skill.UpdatedAt)?.fmt('d LLL yyyy') ?? skill.UpdatedAt,
            className: () => 'text-xs text-neutral-500',
            sortVal: skill => skill.UpdatedAt,
        },
    ];
    const skillFormFields = {
        title: FormType.Text({
            lbl: 'Title',
            maxLength: 40,
            validators: [
                {
                    req: (value) => value.trim().length > 0,
                    msg: 'is required',
                },
            ],
        }),
        description: FormType.Textarea({
            lbl: 'Description',
            rows: 2,
            maxLength: 160,
            disableNewlines: true,
        }),
        document: (() => {
            const field = FormType.Textarea({
                lbl: 'Full Skill',
                fullWidth: true,
                rows: 8,
                maxLength: 10000,
                className: 'flex-1 min-h-0 [&_.cmpt]:h-full [&_.cmpt]:min-h-0 [&_.cmpt]:flex [&_.cmpt]:flex-col',
                validators: [
                    {
                        req: (value) => value.trim().length > 0,
                        msg: 'is required',
                    },
                ],
            });
            return {
                ...field,
                jsx: (...args) => {
                    const [input] = args;
                    const hasTextareaClassName = (value) => typeof value === 'object' &&
                        value != null &&
                        'classNameTextarea' in value;
                    const existingTextareaClassName = hasTextareaClassName(input)
                        ? input.classNameTextarea
                        : undefined;
                    return (React.createElement(TextareaWithVoice, { value: input.value, onUpdate: input.onUpdate, className: BuildClass([
                            'ui5 ui5-textarea tailwind-wrapper w-full align-top h-full js-skill-full-surface font-mono',
                            input.className,
                        ]), classNameTextarea: BuildClass([
                            'font-mono text-[13px] leading-[18px]',
                            existingTextareaClassName,
                        ]), style: {
                            fontFamily: 'Cascadia Code, monospace',
                        }, title: input.title ?? undefined, rows: 8, readOnly: input.readOnly, disabled: input.disabled, onFocus: input.onFocus, onBlur: input.onBlur, "data-1p-ignore": true, prompt: '', maxLength: 10000 }));
                },
            };
        })(),
    };
    const normaliseSkillDescription = (description) => description.replace(/\r?\n/g, ' ');
    const estimatedTokenCount = React.useMemo(() => {
        const composedText = [
            skillFormValue.title ?? '',
            skillFormValue.description ?? '',
            skillFormValue.document ?? '',
        ].join('\n');
        return Math.ceil(composedText.length / 4);
    }, [skillFormValue.description, skillFormValue.document, skillFormValue.title]);
    const SKILL_TOKEN_CAP = 2000;
    const skillTokenTiers = React.useMemo(() => [
        { upTo: 1400, colour: 'oklch(0.62 0 0)' },
        { upTo: 1800, colour: 'oklch(0.72 0.16 65)' },
        { upTo: Number.POSITIVE_INFINITY, colour: 'oklch(0.62 0.23 28)' },
    ], []);
    const saveSkillFromToolbar = React.useCallback(() => {
        if (!selectedSkill || isGlobalReadOnly) {
            setSkillsFormMessage('Global skills are read-only');
            return;
        }
        const title = skillFormValue.title ?? '';
        const description = skillFormValue.description ?? '';
        const document = skillFormValue.document ?? '';
        const validationErrors = _.compact([
            title.trim().length === 0 ? 'Invalid Title: is required' : null,
            title.length > 40 ? 'Invalid Title: must be 40 characters or less' : null,
            description.length > 160
                ? 'Invalid Description: must be 160 characters or less'
                : null,
            document.trim().length === 0 ? 'Invalid Full Skill: is required' : null,
            document.length > 10000
                ? 'Invalid Full Skill: must be 10000 characters or less'
                : null,
        ]);
        if (validationErrors.length > 0) {
            setSkillsFormMessage(validationErrors.join('\n'));
            return;
        }
        setSkillsFormLoading('save');
        setSkillsFormMessage('');
        rs.props.skillsSettingsCallbacks.onUpdateSkill({
            skillID: selectedSkill.ID,
            applicationType: rs.props.applicationType,
            title,
            description: normaliseSkillDescription(description),
            document,
            callbacks: {
                onComplete: () => {
                    setSkillsFormLoading(null);
                    setSkillsFormMode('read');
                },
                onError: err => {
                    setSkillsFormLoading(null);
                    setSkillsFormMessage(err);
                },
            },
        });
    }, [
        isGlobalReadOnly,
        normaliseSkillDescription,
        rs.props.applicationType,
        rs.props.skillsSettingsCallbacks,
        selectedSkill,
        skillFormValue.description,
        skillFormValue.document,
        skillFormValue.title,
    ]);
    const cancelSkillEditFromToolbar = React.useCallback(() => {
        setSkillFormValue(selectedSkillFormValue);
        setSkillsFormMessage('');
        setSkillsFormMode('read');
    }, [selectedSkillFormValue]);
    const deleteSkillFromToolbar = React.useCallback(() => {
        if (!selectedSkill || isGlobalReadOnly) {
            setSkillsFormMessage('Global skills are read-only');
            return;
        }
        PromptConfirm({
            title: 'Delete skill?',
            msg: 'This will permanently delete this skill. This action cannot be undone.',
            size: [320, 190],
            yes: done => {
                setSkillsFormLoading('delete');
                rs.props.skillsSettingsCallbacks.onDeleteSkill({
                    skillID: selectedSkill.ID,
                    applicationType: rs.props.applicationType,
                    callbacks: {
                        onComplete: () => {
                            setSelectedSkillID(null);
                            setSkillsFormMode('read');
                            setSkillsFormLoading(null);
                            setSkillsFormMessage('');
                            done(true);
                        },
                        onError: err => {
                            setSkillsFormLoading(null);
                            setSkillsFormMessage(err);
                            done(true);
                        },
                    },
                });
            },
        });
    }, [
        isGlobalReadOnly,
        rs.props.applicationType,
        rs.props.skillsSettingsCallbacks,
        selectedSkill,
    ]);
    const openCreateSkillFlyout = React.useCallback(() => {
        openFlyoutForm({
            title: 'New Skill',
            size: [640, 760],
            lblWidth: 100,
            formPrompt: 'Create a scoped skill for this workspace',
            defaultValue: () => ({
                title: '',
                description: '',
                document: '',
            }),
            fields: skillFormFields,
            onSave: (model, resolve) => {
                props.skillsSettingsCallbacks.onCreateSkill({
                    facility: props.facility,
                    subFacility: scopedSubFacility,
                    applicationType: props.applicationType,
                    title: model.current.title,
                    description: normaliseSkillDescription(model.current.description),
                    document: model.current.document,
                    callbacks: {
                        onComplete: skill => {
                            const nextSkillID = String(skill.ID);
                            pendingCreatedSkillID.current = nextSkillID;
                            setSelectedSkillID(nextSkillID);
                            resolve(true);
                        },
                        onError: err => {
                            resolve(err);
                        },
                    },
                });
            },
        });
    }, [
        normaliseSkillDescription,
        props.applicationType,
        props.facility,
        props.skillsSettingsCallbacks,
        scopedSubFacility,
        skillFormFields,
    ]);
    const openSkillsToolbarMenu = React.useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const menuItems = [
            {
                label: 'Delete',
                icon: '/static/img/i8/color-trash-delete.svg',
                disabled: skillsFormLoading != null ||
                    !selectedSkill ||
                    Boolean(isGlobalReadOnly),
                onClick: () => {
                    deleteSkillFromToolbar();
                },
            },
        ];
        setContextMenu({
            position: {
                x: rect.left,
                y: rect.bottom,
            },
            items: menuItems,
            width: 180,
        });
    }, [deleteSkillFromToolbar, isGlobalReadOnly, selectedSkill, skillsFormLoading]);
    const composedSkillMarkdown = React.useMemo(() => _.compact([
        skillFormValue.title.trim().length > 0
            ? `# ${skillFormValue.title}`
            : null,
        skillFormValue.description.trim().length > 0
            ? `> ${skillFormValue.description}`
            : null,
        skillFormValue.document,
    ]).join('\n\n'), [skillFormValue.description, skillFormValue.document, skillFormValue.title]);
    const markdownWrapperStyle = React.useMemo(() => getChatCSSVars(undefined, (rs.props.theme ?? 'light') === 'dark'), [rs.props.theme]);
    const showSystemSkillReadNotice = skillsFormMode === 'read' && isInternalUser && Boolean(isGlobalSelected);
    return (React.createElement("div", { className: "h-full min-h-0 flex flex-col" },
        React.createElement(SidebarListGrid, { pk: skill => String(skill.ID), data: visibleSkills, fields: sidebarFields, multiple: false, value: selectedSkillID, onUpdateValue: value => {
                setSelectedSkillID(value);
            }, view: skillsSblgView, onUpdateView: view => {
                setSkillsSblgView(prev => ({ ...prev, ...view }));
            }, sidebarWidth: {
                defaultWidth: 288,
                minWidth: 200,
                maxWidth: 420,
            }, listGridSettings: {
                defaultSortingKey: 'title',
            }, stubMessage: { lbl: 'No skills yet' }, adding: {
                onClick: () => {
                    openCreateSkillFlyout();
                },
            }, buildForm: () => (React.createElement("div", { className: "h-full min-h-0 flex flex-col" },
                React.createElement("div", { className: "shrink-0 border-b border-neutral-200 bg-white" },
                    React.createElement(ToolbarNew, { className: "[&_.side]:flex-nowrap", lhs: [
                            {
                                type: 'raw-jsx',
                                width: 36,
                                content: (React.createElement("div", { className: "w-9 h-8 flex items-center justify-center" },
                                    React.createElement(UsageLimitDonut, { value: estimatedTokenCount, max: SKILL_TOKEN_CAP, tiers: skillTokenTiers, curve: input => input.max > 0
                                            ? input.value / input.max
                                            : 0, tooltipContent: React.createElement("div", { style: {
                                                fontSize: '13px',
                                                lineHeight: '1.2',
                                                textAlign: 'left',
                                            } }, `Skill usage: ${estimatedTokenCount.toLocaleString()} / ${SKILL_TOKEN_CAP.toLocaleString()} tokens`), size: 16, thickness: 4, className: "cursor-help" }))),
                                overflowOverride: {
                                    label: 'Skill usage',
                                    labelDesc: `Skill usage: ${estimatedTokenCount.toLocaleString()} / ${SKILL_TOKEN_CAP.toLocaleString()} tokens`,
                                },
                            },
                        ], rhs: [
                            skillsFormMode === 'read'
                                ? {
                                    key: 'skills-edit',
                                    type: 'button-24',
                                    img: '/static/img/i8/color-edit.svg',
                                    lbl: 'Edit',
                                    classNameImg: 'w-4 h-4 my-[7px] mr-1.5',
                                    classNameSpan: 'whitespace-nowrap',
                                    title: 'Edit',
                                    width: 74,
                                    disabled: !selectedSkill ||
                                        Boolean(isGlobalReadOnly),
                                    onClick: () => {
                                        setSkillsFormMode('edit');
                                        requestAnimationFrame(() => {
                                            fullSkillTextareaRef.current =
                                                skillsEditFormRef.current?.querySelector('.js-skill-full-surface textarea') ?? null;
                                            fullSkillTextareaRef.current?.focus();
                                        });
                                    },
                                }
                                : {
                                    key: 'skills-save',
                                    type: 'button-24',
                                    img: '/static/img/i8/ios16-tick.svg',
                                    lbl: 'Save',
                                    classNameImg: 'w-4 h-4 my-[7px] mr-1.5',
                                    classNameSpan: 'whitespace-nowrap',
                                    title: 'Save',
                                    width: 76,
                                    disabled: skillsFormLoading != null ||
                                        !selectedSkill ||
                                        Boolean(isGlobalReadOnly),
                                    onClick: saveSkillFromToolbar,
                                },
                            skillsFormMode === 'read'
                                ? {
                                    key: 'skills-kebab',
                                    type: 'button-24',
                                    img: '/static/img/svg/kebab.svg',
                                    className: 'px-1.5',
                                    classNameImg: 'w-4 h-4 my-[7px] mr-0',
                                    title: 'More actions',
                                    width: 28,
                                    onClick: openSkillsToolbarMenu,
                                }
                                : null,
                            skillsFormMode === 'edit'
                                ? {
                                    key: 'skills-cancel',
                                    type: 'button-24',
                                    img: '/static/img/i8/ios16-refresh.svg',
                                    lbl: 'Cancel',
                                    classNameImg: 'w-4 h-4 my-[7px] mr-1.5',
                                    classNameSpan: 'whitespace-nowrap',
                                    title: 'Cancel',
                                    width: 80,
                                    disabled: skillsFormLoading != null,
                                    onClick: cancelSkillEditFromToolbar,
                                }
                                : null,
                        ] }),
                    skillsFormMessage ? (React.createElement("div", { className: "px-4 pb-2 text-sm text-red-600 whitespace-pre-wrap" }, skillsFormMessage)) : null),
                skillsFormMode === 'edit' ? (React.createElement("div", { ref: skillsEditFormRef, className: "flex-1 min-h-0 flex flex-col" },
                    React.createElement(FormComponent, { key: `skill-form-${selectedSkillID ?? 'none'}`, className: "h-full min-h-0 flex flex-col overflow-hidden", fields: skillFormFields, formPrompt: "Managing skills for agent automation", lblWidth: 100, defaultValue: () => selectedSkillFormValue, value: skillFormValue, disableFormFill: true, excludeButtons: true, disabled: Boolean(isGlobalReadOnly), onUpdate: setSkillFormValue, onSave: (_model, resolve) => {
                            resolve(true);
                        } }))) : (React.createElement("div", { className: "flex-1 overflow-auto p-3" },
                    showSystemSkillReadNotice ? (React.createElement("div", { className: "mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" }, "This is a system-level skill and is not visible to external users.")) : null,
                    React.createElement("div", { className: "w-full px-1 py-1", style: markdownWrapperStyle },
                        React.createElement("div", { className: "text-left select-text [&_h1]:!h-[28px] [&_h1]:!leading-[28px] [&_h1]:!text-[24px]" },
                            React.createElement(MarkdownRenderer, { content: composedSkillMarkdown, largeText: true }))))))) })));
};
const TaskDetailPane = ({ rs, taskID, taskDetails, }) => {
    const props = rs.props;
    const checklist = props.data.taskChecklists[taskID] ?? [];
    const attachments = props.data.taskAttachments[taskID] ?? [];
    const discussionItems = props.data.taskDiscussionItems[taskID] ?? [];
    const taskThreads = _.pick(props.data.agentThreads, taskDetails.ThreadIDs ?? []);
    const allTaskPendingChanges = _.chain(taskThreads)
        .flatMap(t => t.pendingChanges)
        .value();
    const tabs = [
        {
            value: 'details',
            label: 'Details',
            content: () => (React.createElement(DetailsTab, { rs: rs, taskID: taskID, taskDetails: taskDetails, checklist: checklist, attachments: attachments })),
        },
        {
            value: 'discussion',
            label: 'Discussion',
            content: () => (React.createElement(DiscussionTab, { rs: rs, taskID: taskID, discussionItems: discussionItems, attachments: attachments, taskThreads: taskThreads })),
        },
        {
            value: 'review',
            label: `Review${allTaskPendingChanges.length > 0 ? ` (${allTaskPendingChanges.length})` : ''}`,
            content: () => (React.createElement(ReviewTab, { rs: rs, taskThreads: taskThreads, allPendingChanges: allTaskPendingChanges })),
        },
    ];
    return (React.createElement("div", { className: "h-full flex flex-col" },
        React.createElement("div", { className: "border-b border-neutral-200 px-4 py-2" },
            React.createElement("div", { className: "flex items-center gap-2" },
                React.createElement("h2", { className: "text-lg font-medium flex-1 truncate" }, taskDetails.Title ?? 'Untitled Task'),
                React.createElement("span", { className: BuildClass({
                        'text-xs px-2 py-1 rounded': true,
                        'bg-green-100 text-green-700': taskDetails.Status === 900,
                        'bg-blue-100 text-blue-700': taskDetails.Status >= 100 && taskDetails.Status < 200,
                        'bg-yellow-100 text-yellow-700': taskDetails.Status >= 200 && taskDetails.Status < 900,
                        'bg-red-100 text-red-700': taskDetails.Status === 80 ||
                            (taskDetails.Status >= 601 && taskDetails.Status <= 603),
                        'bg-neutral-100 text-neutral-700': taskDetails.Status === 50,
                    }) }, STATUS_LABELS[taskDetails.Status] ?? 'Unknown'))),
        React.createElement(TabControl, { tabs: tabs, value: rs.state.currentTab, onUpdate: v => {
                rs.updateState({ currentTab: v });
            }, className: "flex-1" })));
};
const DetailsTab = ({ rs, taskID, taskDetails, checklist, attachments, }) => {
    const props = rs.props;
    const formatChecklistTimestamp = (timestamp) => {
        if (!timestamp) {
            return '';
        }
        return DateTimeObj.parse(timestamp)?.fmt('d LLL yyyy HH:mm') ?? timestamp;
    };
    React.useEffect(() => {
        rs.updateState({
            draftPrimaryNote: taskDetails.PrimaryNote,
            draftSecondaryNote: taskDetails.SecondaryNote ?? '',
            isPrimaryNoteEditing: false,
            isSecondaryNoteEditing: false,
        });
    }, [taskID, taskDetails.PrimaryNote, taskDetails.SecondaryNote, rs]);
    const savePrimaryNote = () => {
        props.taskCallbacks.onUpdateTask({
            taskID,
            updates: { PrimaryNote: rs.state.draftPrimaryNote },
            callbacks: {
                onComplete: () => {
                    rs.updateState({ isPrimaryNoteEditing: false });
                },
                onError: err => {
                    console.error('Failed to save note:', err);
                },
            },
        });
    };
    const saveSecondaryNote = () => {
        props.taskCallbacks.onUpdateTask({
            taskID,
            updates: { SecondaryNote: rs.state.draftSecondaryNote },
            callbacks: {
                onComplete: () => {
                    rs.updateState({ isSecondaryNoteEditing: false });
                },
                onError: err => {
                    console.error('Failed to save note:', err);
                },
            },
        });
    };
    const selectedAttachment = rs.state.selectedAttachmentID
        ? attachments.find(a => a.ID === rs.state.selectedAttachmentID)
        : null;
    const cancelPrimaryEdit = () => {
        rs.updateState({
            isPrimaryNoteEditing: false,
            draftPrimaryNote: taskDetails.PrimaryNote,
        });
    };
    const cancelSecondaryEdit = () => {
        rs.updateState({
            isSecondaryNoteEditing: false,
            draftSecondaryNote: taskDetails.SecondaryNote ?? '',
        });
    };
    return (React.createElement("div", { className: "h-full flex flex-col" },
        React.createElement(ToolbarNew, { className: "border-b border-neutral-200", lhs: [
                {
                    type: 'label',
                    lbl: 'Notes:',
                    width: 50,
                },
                {
                    type: 'button',
                    lbl: rs.state.isPrimaryNoteEditing
                        ? 'Save Primary'
                        : 'Edit Primary',
                    width: rs.state.isPrimaryNoteEditing ? 95 : 90,
                    onClick: () => {
                        if (rs.state.isPrimaryNoteEditing) {
                            savePrimaryNote();
                        }
                        else {
                            rs.updateState({ isPrimaryNoteEditing: true });
                        }
                    },
                },
                rs.state.isPrimaryNoteEditing
                    ? {
                        type: 'button',
                        lbl: 'Cancel',
                        width: 60,
                        onClick: cancelPrimaryEdit,
                    }
                    : null,
                {
                    type: 'button',
                    lbl: rs.state.isSecondaryNoteEditing
                        ? 'Save Secondary'
                        : 'Edit Secondary',
                    width: rs.state.isSecondaryNoteEditing ? 110 : 105,
                    onClick: () => {
                        if (rs.state.isSecondaryNoteEditing) {
                            saveSecondaryNote();
                        }
                        else {
                            rs.updateState({ isSecondaryNoteEditing: true });
                        }
                    },
                },
                rs.state.isSecondaryNoteEditing
                    ? {
                        type: 'button',
                        lbl: 'Cancel',
                        width: 60,
                        onClick: cancelSecondaryEdit,
                    }
                    : null,
            ], rhs: [] }),
        React.createElement("div", { className: "flex-1 overflow-y-auto p-4 space-y-6" },
            React.createElement("div", null,
                React.createElement("h3", { className: "text-sm font-medium mb-2" }, "Primary Note"),
                rs.state.isPrimaryNoteEditing ? (React.createElement(Textarea, { value: rs.state.draftPrimaryNote, onUpdate: v => {
                        rs.updateState({ draftPrimaryNote: v });
                    }, className: "w-full min-h-[150px]" })) : (React.createElement("div", { className: "p-3 bg-neutral-50 rounded border border-neutral-200 min-h-[100px]" }, taskDetails.PrimaryNote ? (React.createElement(MarkdownRenderer, { content: taskDetails.PrimaryNote })) : (React.createElement("span", { className: "text-neutral-400" }, "No primary note"))))),
            React.createElement("div", null,
                React.createElement("h3", { className: "text-sm font-medium mb-2" },
                    "Secondary Note",
                    ' ',
                    React.createElement("span", { className: "text-neutral-400 font-normal" }, "(Agent Scratchpad)")),
                rs.state.isSecondaryNoteEditing ? (React.createElement(Textarea, { value: rs.state.draftSecondaryNote, onUpdate: v => {
                        rs.updateState({ draftSecondaryNote: v });
                    }, className: "w-full min-h-[100px]" })) : (React.createElement("div", { className: "p-3 bg-neutral-50 rounded border border-neutral-200 min-h-[60px]" }, taskDetails.SecondaryNote ? (React.createElement(MarkdownRenderer, { content: taskDetails.SecondaryNote })) : (React.createElement("span", { className: "text-neutral-400" }, "No secondary note"))))),
            React.createElement("div", null,
                React.createElement("h3", { className: "text-sm font-medium mb-2" }, "Checklist"),
                checklist.length === 0 ? (React.createElement("div", { className: "text-sm text-neutral-500" }, "No checklist items")) : (React.createElement("div", { className: "space-y-1" }, checklist.map(item => (React.createElement("div", { key: item.ID, className: "flex items-center gap-2 p-2 bg-neutral-50 rounded border border-neutral-200" },
                    React.createElement(Checkbox, { lbl: "", value: item.VerifiedCompleteAt != null, onUpdate: () => {
                            if (item.VerifiedCompleteAt) {
                                props.checklistCallbacks.onUnverifyChecklistItem({
                                    itemID: item.ID,
                                    callbacks: {
                                        onComplete: () => { },
                                        onError: err => {
                                            console.error(err);
                                        },
                                    },
                                });
                            }
                            else {
                                props.checklistCallbacks.onVerifyChecklistItem({
                                    itemID: item.ID,
                                    callbacks: {
                                        onComplete: () => { },
                                        onError: err => {
                                            console.error(err);
                                        },
                                    },
                                });
                            }
                        } }),
                    React.createElement("span", { className: BuildClass({
                            'flex-1 text-sm': true,
                            'line-through text-neutral-400': item.VerifiedCompleteAt != null,
                        }) }, item.ItemName),
                    item.ClaimedCompleteAt && (React.createElement("span", { className: "text-xs text-yellow-600" },
                        "Claimed",
                        ' ',
                        formatChecklistTimestamp(item.ClaimedCompleteAt))),
                    item.VerifiedCompleteAt && (React.createElement("span", { className: "text-xs text-green-600" },
                        "Verified",
                        ' ',
                        formatChecklistTimestamp(item.VerifiedCompleteAt))))))))),
            React.createElement("div", null,
                React.createElement("h3", { className: "text-sm font-medium mb-2" }, "Attachments"),
                attachments.length === 0 ? (React.createElement("div", { className: "text-sm text-neutral-500" }, "No attachments")) : (React.createElement("div", { className: "h-64" },
                    React.createElement(ResizeSplitter, { value: rs.state.attachmentSplitWidths, onUpdate: v => {
                            rs.updateState({ attachmentSplitWidths: v });
                        }, panes: [
                            {
                                defaultWidth: 200,
                                minWidth: 150,
                                maxWidth: 300,
                                collapsible: false,
                                flexWidth: null,
                                content: (React.createElement("div", { className: "h-full overflow-y-auto space-y-1 p-1" }, attachments.map(att => (React.createElement("div", { key: att.ID, className: BuildClass({
                                        'flex items-center gap-2 p-2 rounded cursor-pointer': true,
                                        'bg-blue-50': rs.state
                                            .selectedAttachmentID ===
                                            att.ID,
                                        'hover:bg-neutral-50': rs.state
                                            .selectedAttachmentID !==
                                            att.ID,
                                    }), onClick: () => {
                                        rs.updateState({
                                            selectedAttachmentID: att.ID,
                                        });
                                    } },
                                    React.createElement("span", { className: "flex-1 text-sm truncate" }, att.Filename ?? att.FileGUID),
                                    React.createElement("span", { className: "text-xs text-neutral-500" }, att.MimeType.split('/')[1])))))),
                            },
                            {
                                defaultWidth: null,
                                minWidth: null,
                                maxWidth: null,
                                collapsible: false,
                                flexWidth: 1,
                                content: (React.createElement("div", { className: "h-full border-l border-neutral-200" }, selectedAttachment ? (React.createElement(NoteAttachment, { attachment: {
                                        Checksum: selectedAttachment.FileGUID,
                                        MimeType: selectedAttachment.MimeType,
                                    } })) : (React.createElement("div", { className: "h-full flex items-center justify-center text-neutral-500 text-sm" }, "Select an attachment to preview")))),
                            },
                        ] })))))));
};
const discussionAttachmentToBlock = (input) => {
    const attachment = input.attachmentsByGuid[input.attachmentGuid];
    const filename = attachment?.Filename ?? 'Attachment';
    const mimeType = attachment?.MimeType ?? 'application/octet-stream';
    if (mimeType.startsWith('image/')) {
        return {
            role: 'user',
            timestamp: input.timestamp,
            type: 'image',
            image: {
                guid: input.attachmentGuid,
                filename,
                width: 0,
                height: 0,
            },
        };
    }
    return {
        role: 'user',
        timestamp: input.timestamp,
        type: 'file',
        file: {
            guid: input.attachmentGuid,
            filename,
            mimeType,
            size: 0,
        },
    };
};
const discussionToThread = (items, taskID, attachments) => {
    const attachmentsByGuid = _.keyBy(attachments, 'FileGUID');
    const content = _.flatMap(items, item => {
        const textBlocks = item.Text.trim().length > 0
            ? [
                {
                    role: 'user',
                    timestamp: item.Timestamp,
                    type: 'text',
                    text: item.Text,
                },
            ]
            : [];
        const attachmentBlocks = item.FileGuid
            ? [
                discussionAttachmentToBlock({
                    attachmentGuid: item.FileGuid,
                    timestamp: item.Timestamp,
                    attachmentsByGuid,
                }),
            ]
            : [];
        return [...textBlocks, ...attachmentBlocks];
    });
    const firstItem = items[0];
    const firstAttachment = firstItem?.FileGuid
        ? attachmentsByGuid[firstItem.FileGuid]
        : null;
    const firstMessagePreview = (() => {
        if (!firstItem) {
            return null;
        }
        if (firstItem.Text.trim().length > 0) {
            return firstItem.Text;
        }
        return firstAttachment?.Filename ?? null;
    })();
    return {
        metadata: {
            id: `human-${taskID}`,
            agentType: 'human',
            facility: null,
            subFacility: null,
            user: null,
            title: 'Team Discussion',
            parentTask: taskID,
            status: 'active',
            createdAt: items[0]?.Timestamp ?? new Date().toISOString(),
            updatedAt: items[items.length - 1]?.Timestamp ?? new Date().toISOString(),
        },
        firstMessagePreview,
        pendingChangesCount: 0,
        pendingQuestionsCount: 0,
        contentCount: content.length,
        completionCount: 0,
        content,
        pendingChanges: [],
        pendingQuestions: [],
        completions: [],
    };
};
const base64ToDiscussionFile = (base64, filename, mimeType) => {
    const bytes = Uint8Array.from(atob(base64), char => char.charCodeAt(0));
    return new File([bytes], filename, { type: mimeType });
};
const discussionMessageToFile = (blocks) => {
    const attachmentBlock = _.find(blocks, block => block.type === 'file-pending' || block.type === 'image-pending');
    if (!attachmentBlock) {
        return null;
    }
    if (attachmentBlock.type === 'file-pending') {
        return base64ToDiscussionFile(attachmentBlock.file.data, attachmentBlock.file.filename, attachmentBlock.file.mimeType);
    }
    return base64ToDiscussionFile(attachmentBlock.image.data, attachmentBlock.image.filename, 'image/webp');
};
const DiscussionTab = ({ rs, taskID, discussionItems, attachments, taskThreads, }) => {
    const props = rs.props;
    const humanThreadID = `human-${taskID}`;
    const humanThread = discussionToThread(discussionItems, taskID, attachments);
    return (React.createElement("div", { className: "h-full flex flex-col" },
        React.createElement("div", { className: "border-b border-neutral-200 bg-neutral-50 h-[350px] flex flex-col" },
            React.createElement("div", { className: "px-3 py-2 border-b border-neutral-200" },
                React.createElement("h3", { className: "text-sm font-medium" }, "Team Discussion")),
            React.createElement("div", { className: "flex-1 overflow-hidden" },
                React.createElement(AgentUI, { threads: { [humanThreadID]: humanThread }, currentUserID: props.currentUserID, facility: props.facility, subFacility: props.subFacility ?? 0, agentType: "human", changeRenderers: props.changeRenderers, widgetToGroupCode: props.widgetToGroupCode, theme: props.theme, hue: props.hue, hideSidebar: true, placeholder: "Post to team discussion...", onSendMessage: input => {
                        const text = input.message
                            .filter(m => m.type === 'text')
                            .map(m => ('text' in m ? m.text : ''))
                            .join('\n');
                        props.discussionCallbacks.onPostDiscussion({
                            taskID,
                            text,
                            file: discussionMessageToFile(input.message) ?? undefined,
                            callbacks: {
                                onComplete: () => {
                                    input.callbacks.onComplete({});
                                },
                                onError: err => {
                                    input.callbacks.onError(err);
                                },
                            },
                        });
                    }, onApproveChanges: () => { }, onRejectChanges: () => { } }))),
        React.createElement("div", { className: "flex-1 overflow-y-auto" },
            React.createElement("div", { className: "p-3" },
                React.createElement("h3", { className: "text-sm font-medium mb-2" }, "Agent Threads")),
            Object.keys(taskThreads).length === 0 ? (React.createElement("div", { className: "p-3 text-sm text-neutral-500 text-center" }, "No agent threads for this task")) : (React.createElement("div", { className: "space-y-4 px-3 pb-3" }, _.values(taskThreads).map(thread => (React.createElement("div", { key: thread.metadata.id, className: "border border-neutral-200 rounded overflow-hidden" },
                React.createElement("div", { className: "p-2 bg-neutral-50 border-b border-neutral-200" },
                    React.createElement("span", { className: "text-sm font-medium" }, thread.metadata.title ?? 'Agent Thread'),
                    React.createElement("span", { className: "text-xs text-neutral-500 ml-2" }, thread.metadata.agentType)),
                React.createElement("div", { className: "h-[400px]" },
                    React.createElement(AgentUI, { threads: { [thread.metadata.id]: thread }, currentUserID: props.currentUserID, facility: props.facility, subFacility: props.subFacility ?? 0, agentType: thread.metadata.agentType, changeRenderers: props.changeRenderers, widgetToGroupCode: props.widgetToGroupCode, theme: props.theme, hue: props.hue, isReadOnly: true, hideSidebar: true, readOnlyMessage: "This thread is read-only. Use the Discussion section above for feedback.", onSendMessage: () => { }, onApproveChanges: () => { }, onRejectChanges: () => { }, onLoadThreadContent: props.agentThreadCallbacks.onLoadThreadContent }))))))))));
};
const ReviewTab = ({ rs, taskThreads, allPendingChanges, }) => {
    const props = rs.props;
    const isDarkMode = (props.theme ?? 'light') === 'dark';
    const changesWithStatus = allPendingChanges.map(c => ({
        ...c,
        status: rs.state.pendingChangesStates[c.id] ?? getPendingChangeStatus(c),
        createdAt: '',
    }));
    const executingIDs = new Set(changesWithStatus.filter(c => c.status === 'executing').map(c => c.id));
    const updateChangeStates = (updates) => {
        rs.updateState(s => ({
            pendingChangesStates: { ...s.pendingChangesStates, ...updates },
        }));
    };
    const updateChangeState = (id, status) => {
        updateChangeStates({ [id]: status });
    };
    const handleApprove = async (changeID) => {
        const thread = _.values(taskThreads).find(t => t.pendingChanges.some(pc => pc.id === changeID));
        if (!thread) {
            return;
        }
        updateChangeState(changeID, 'executing');
        try {
            await new Promise((resolve, reject) => {
                props.agentThreadCallbacks.onApproveChanges({
                    threadIDs: [thread.metadata.id],
                    approvalIDs: [changeID],
                    callbacks: {
                        onComplete: result => {
                            if (result.errors && result.errors.length > 0) {
                                updateChangeState(changeID, 'error');
                                reject(new Error(result.errors[0]?.error));
                            }
                            else {
                                updateChangeState(changeID, 'approved');
                                resolve();
                            }
                        },
                        onError: err => {
                            updateChangeState(changeID, 'error');
                            reject(new Error(err));
                        },
                    },
                });
            });
        }
        catch {
            updateChangeState(changeID, 'error');
        }
    };
    const handleReject = (changeID) => {
        updateChangeState(changeID, 'rejected');
        props.agentThreadCallbacks.onRejectChanges({
            approvalIDs: [changeID],
            callbacks: {
                onComplete: threads => {
                    updateChangeState(changeID, hasPendingApproval(threads, changeID) ? 'pending' : 'rejected');
                },
            },
        });
    };
    const handleApproveAll = async (changeIDs) => {
        const pending = changesWithStatus.filter(change => changeIDs.includes(change.id) && change.status === 'pending');
        if (pending.length === 0) {
            return;
        }
        updateChangeStates(_.chain(pending)
            .map(c => [c.id, 'executing'])
            .fromPairs()
            .value());
        const changesByThread = _.chain(pending)
            .map(c => {
            const thread = _.values(taskThreads).find(t => t.pendingChanges.some(pc => pc.id === c.id));
            return thread ? { changeID: c.id, threadID: thread.metadata.id } : null;
        })
            .compact()
            .groupBy('threadID')
            .value();
        await Promise.all(Object.entries(changesByThread).map(async ([threadID, changes]) => {
            const approvalIDs = changes.map(c => c.changeID);
            return new Promise((resolve, reject) => {
                props.agentThreadCallbacks.onApproveChanges({
                    threadIDs: [threadID],
                    approvalIDs,
                    callbacks: {
                        onComplete: result => {
                            if (result.errors && result.errors.length > 0) {
                                updateChangeStates(_.chain(approvalIDs)
                                    .map(id => [id, 'pending'])
                                    .fromPairs()
                                    .value());
                                reject(new Error('Some changes failed'));
                            }
                            else {
                                updateChangeStates(_.chain(approvalIDs)
                                    .map(id => [id, 'approved'])
                                    .fromPairs()
                                    .value());
                                resolve();
                            }
                        },
                        onError: () => {
                            updateChangeStates(_.chain(approvalIDs)
                                .map(id => [id, 'pending'])
                                .fromPairs()
                                .value());
                            reject(new Error('Approval failed'));
                        },
                    },
                });
            }).catch(() => {
            });
        }));
    };
    const handleRejectAll = (changeIDs) => {
        const pendingIDs = changesWithStatus
            .filter(change => changeIDs.includes(change.id) && change.status === 'pending')
            .map(c => c.id);
        _.forEach(pendingIDs, id => {
            handleReject(id);
        });
    };
    return (React.createElement("div", { className: "h-full flex flex-col" },
        React.createElement("div", { className: "flex-1 relative min-h-0" },
            React.createElement("div", { className: "absolute inset-0 pointer-events-none z-0", style: {
                    backgroundColor: isDarkMode
                        ? 'oklch(0 0 0 / 0.9)'
                        : 'oklch(0 0 0 / 0.1)',
                } }),
            React.createElement("div", { className: "absolute inset-0 pointer-events-none z-0", style: {
                    backgroundImage: 'url(/static/img/svg/window-background-texture.svg)',
                    backgroundRepeat: 'repeat',
                    backgroundSize: '5px',
                    filter: isDarkMode
                        ? 'hue-rotate(200deg) invert(1) brightness(0.9)'
                        : 'hue-rotate(260deg) grayscale(50%)',
                    opacity: isDarkMode ? 0.15 : 0.2,
                } }),
            React.createElement("div", { className: "absolute inset-0 overflow-y-scroll z-10 flex flex-col px-1" },
                React.createElement("div", { className: "w-full max-w-[800px] mx-auto pt-4 pb-4" }, changesWithStatus.length > 0 ? (React.createElement(PendingChangesTray, { changes: changesWithStatus, renderers: props.changeRenderers, toolHandlers: {}, onApprove: ids => {
                        if (ids.length === 1) {
                            const changeID = ids[0];
                            if (!changeID) {
                                return;
                            }
                            handleApprove(changeID).catch(console.error);
                            return;
                        }
                        handleApproveAll(ids).catch(console.error);
                    }, onReject: ids => {
                        if (ids.length === 1) {
                            const changeID = ids[0];
                            if (!changeID) {
                                return;
                            }
                            handleReject(changeID);
                            return;
                        }
                        handleRejectAll(ids);
                    }, executingIDs: executingIDs, disabled: false, widgetToGroupCode: props.widgetToGroupCode })) : (React.createElement("div", { className: "flex items-center justify-center py-8" },
                    React.createElement("p", { className: "text-sm text-neutral-500" }, "No pending changes for this task"))))))));
};
//# sourceMappingURL=index.js.map